import React, { useState, useEffect } from 'react';
import { CheckCircle, Globe } from 'lucide-react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from 'react-helmet';
import CookieConsent from './CookieConsent';
import GDPR from './GDPR';
import ContactForm from './ContactForm';
import NotFound from './NotFound';
import { logPageView, trackEvent } from '../utils/analytics';



const ImageStrip = ({ language }) => {
  const t = translations[language];
  // Initialize with first 3 images for SSR/SEO/ReactSnap stability
  const [displayImages, setDisplayImages] = useState(imagePool.slice(0, 3));
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    // Only randomize in the browser, not during pre-rendering
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes("ReactSnap")) return;

    // 1. Create a copy of the pool
    const pool = [...imagePool];

    // 2. Fisher-Yates Shuffle
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    // 3. Select top 3 unique images
    setDisplayImages(pool.slice(0, 3));
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    if (selectedImage) {
      window.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  const handleImageError = (e) => {
    e.target.src = e.target.dataset.fallback;
    // Set fallback title/alt if missing or failed, using a generic localized message or just English
    // Since we don't have easy access to the current language here in the context of the error handler 
    // without passing it down or using a hook, we will set a generic message or check the data-lang attribute if we add one.
    // For now, simple fallback.
    if (!e.target.alt) e.target.alt = "Image could not be loaded";
  };

  // No longer return null, so content is always present

  return (
    <>
      <div className="w-full bg-blue-50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {displayImages.map((image, index) => (
              <div
                key={index}
                className="relative h-64 md:h-80 group overflow-hidden rounded-lg cursor-pointer transform hover:-translate-y-1 transition-all duration-300 shadow-md hover:shadow-xl"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.src}
                  data-fallback={image.fallback}
                  alt={typeof image.alt === 'object' ? image.alt[language] || image.alt['en'] : image.alt}
                  onError={handleImageError}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onClick={() => trackEvent("Engagement", "Image Click", image.title || "Pipe System")}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 text-center">
                  <h3 className="text-xl font-bold mb-2">{image.title}</h3>
                  <span className="inline-block bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-1.5 rounded-full text-sm font-medium transition-colors border border-white/30">
                    {t.enlarge || "Zvětšit"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-8 animation-fade-in"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-full p-2 z-[70]"
            onClick={() => setSelectedImage(null)}
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div
            className="relative max-w-7xl max-h-[90vh] w-full flex flex-col items-center"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={typeof selectedImage.alt === 'object' ? selectedImage.alt[language] || selectedImage.alt['en'] : selectedImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
              onError={handleImageError}
              data-fallback={selectedImage.fallback}
            />
            {selectedImage.title && (
              <p className="text-white text-center mt-6 text-2xl font-semibold tracking-wide">{selectedImage.title}</p>
            )}
            {selectedImage.alt && !selectedImage.title && (
              <p className="text-gray-400 text-center mt-4">
                {typeof selectedImage.alt === 'object' ? selectedImage.alt[language] || selectedImage.alt['en'] : selectedImage.alt}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Pool of images to choose from. 
// Add more images here to have them randomly appear in the strip.
const imagePool = [
  {
    title: "Pipe system",
    alt: {
      en: "Industrial Photo by CHUTTERSNAP on Unsplash",
      cs: "Průmyslová fotografie od CHUTTERSNAP na Unsplash",
      de: "Industriefotografie von CHUTTERSNAP auf Unsplash"
    },
    src: process.env.PUBLIC_URL + "/img1.jpg",
    fallback: "/api/placeholder/800/600"
  },
  {
    title: "Pipe system",
    alt: {
      en: "Photo by CHUTTERSNAP on Unsplash",
      cs: "Fotografie od CHUTTERSNAP na Unsplash",
      de: "Foto von CHUTTERSNAP auf Unsplash"
    },
    src: process.env.PUBLIC_URL + "/img2.jpg",
    fallback: "/api/placeholder/800/600"
  },
  {
    title: "Pipe system",
    alt: {
      en: "Photo by CHUTTERSNAP on Unsplash",
      cs: "Fotografie od CHUTTERSNAP na Unsplash",
      de: "Foto von CHUTTERSNAP auf Unsplash"
    },
    src: process.env.PUBLIC_URL + "/img3.jpg",
    fallback: "/api/placeholder/800/600"
  },
  {
    title: "LP steam system header",
    alt: {
      en: "LP steam system",
      cs: "NT parní systém",
      de: "ND-Dampfsystem"
    },
    src: process.env.PUBLIC_URL + "/img4.png",
    fallback: "/api/placeholder/800/600"
  },
  {
    title: "LP steam system",
    alt: {
      en: "LP steam system",
      cs: "NT parní systém",
      de: "ND-Dampfsystem"
    },
    src: process.env.PUBLIC_URL + "/img5.png",
    fallback: "/api/placeholder/800/600"
  }
];




// SEO metadata for different languages
const seoData = {
  en: {
    title: "BEYSTRENG - Pipe Stress Analysis, Routing Optimization & IT services",
    description: "BEYSTRENG s.r.o. || Professional pipe stress analysis, engineering services, and technical consultations. Over 10 years of experience.",
    keywords: "pipe stress analysis, engineering services, technical consultations, CAESAR II, Rohr2, industrial engineering, piping design, FEM calculations, pressure vessel analysis, pipe routing optimization, pipe class verification, custom software development, IT services, automation, mechanical engineering, EN 13480, ASME B31.1, ASME B31.3, flange calculation EN 1591, energy sector, chemical industry, petrochemical, project coordination, stress engineer, Pilsen, Czech Republic, Europe",
    organization: {
      name: "BEYSTRENG s.r.o.",
      logo: "https://beystreng.com/logo.svg",
      address: {
        streetAddress: "Na Kovárně 951/27",
        addressLocality: "Plzeň",
        postalCode: "312 00",
        addressCountry: "CZ"
      },
      email: "info@beystreng.com"
    }
  },
  cs: {
    title: "BEYSTRENG - Potrubní výpočty, optimalizace trasování a IT služby",
    description: "BEYSTRENG s.r.o. || Profesionální napěťová analýza potrubí, inženýrské služby a technické konzultace. Více než 10 let zkušeností.",
    keywords: "potrubní výpočty, napěťová analýza potrubí, pevnostní výpočty potrubí, inženýrské služby, technické konzultace, CAESAR II, Rohr2, průmyslové inženýrství, návrh potrubí, MKP výpočty, analýza tlakových nádob, optimalizace trasování potrubí, ověření potrubních tříd, vývoj softwaru na míru, IT služby, automatizace, strojírenství, EN 13480, ASME B31.1, ASME B31.3, výpočty přírub EN 1591, energetika, chemický průmysl, petrochemie, koordinace projektů, stress engineer, Plzeň, Česká republika, Evropa",
    organization: {
      name: "BEYSTRENG s.r.o.",
      logo: "https://beystreng.com/logo.svg",
      address: {
        streetAddress: "Na Kovárně 951/27",
        addressLocality: "Plzeň",
        postalCode: "312 00",
        addressCountry: "CZ"
      },
      email: "info@beystreng.com"
    }
  },
  de: {
    title: "BEYSTRENG - Rohrspannungsanalyse, Trassierungsoptimierung & IT-Dienstleistungen",
    description: "BEYSTRENG s.r.o. || Professionelle Rohrstatik, Spannungsanalyse und Ingenieurdienstleistungen. Über 10 Jahre Erfahrung.",
    keywords: "Rohrstatik, Rohrspannungsanalyse, Rohrleitungsberechnung, Rohrleitungsengineering, Technische Beratung, CAESAR II, Rohr2, Industrieanlagenbau, Rohrleitungsplanung, FEM-Berechnungen, Druckbehälteranalyse, Trassierungsoptimierung, Rohrklassenverifizierung, Softwareentwicklung, IT-Dienstleistungen, Maschinenbau, EN 13480, ASME B31.1, ASME B31.3, Flanschberechnungen EN 1591, Energiesektor, Chemische Industrie, Pilsen, Tschechien, Europa",
    organization: {
      name: "BEYSTRENG s.r.o.",
      legalName: "BEYSTRENG s.r.o.",
      taxID: "CZ21421005",
      logo: "https://www.beystreng.com/logo.svg",
      image: "https://www.beystreng.com/logo.svg",
      foundingDate: "2024",
      address: {
        streetAddress: "Na Kovárně 951/27",
        addressLocality: "Plzeň",
        postalCode: "312 00",
        addressCountry: "CZ"
      },
      email: "info@beystreng.com",
      url: "https://www.beystreng.com",
      vatID: "CZ21421005"
    }
  }
};



const translations = {
  en: {
    hero: {
      subtitle: "Technical Analyses and Piping Engineering",
      cta: "Get Started"
    },
    helpSection: {
      title: "Key Services Overview",
      items: [
        {
          title: "Pipe Stress & Flexibility Analysis",
          description: "We specialize in pipe stress and flexibility analysis, providing complex solutions from the conceptual stage through basic and detailed engineering.",
          icon: "🏭"
        },
        {
          title: "Training & Consulting",
          description: "We offer training courses and personalized consultations in piping analysis and engineering.",
          icon: "⚡"
        },
        {
          title: "Specialized Services",
          description: "Our team brings extensive experience in stress analysis coordination and the development of custom software tools tailored to your engineering needs.",
          icon: "🏢"
        }
      ]

    },
    services: {
      title: "Key Services details",
      items: [
        {
          title: "Pipe Stress & Flexibility Analysis",
          description: "Comprehensive pipe stress and flexibility analysis in line with EN 13480‑3, ASME B31.1, and ASME B31.3. We deliver precise FEM evaluations, including flange tightness checks, nozzle load assessments, and a complete range of engineering services that ensure safe, reliable, and efficient piping systems.",
          icon: "📊"
        },
        {
          title: "Routing & Support Optimization",
          description: "Optimizing pipe routes and support systems to manage supports span and thermal expansion. We focus on minimizing stress, nozzle loads and selecting the most cost-effective support solutions while maintaining safety.",
          icon: "🔧"
        },
        {
          title: "Pipe Class Design",
          description: "Design and verification of Piping Class Specifications. We perform all necessary strength calculations for compliance with European and international standards.",
          icon: "⚡"
        },
        {
          title: "Consulting & Training",
          description: "Practical advice on stress analysis workflows and problem-solving. We offer training for major software tools like ROHR2, CAESAR II, and LICAD, tailored to your team's needs.",
          icon: "💡"
        },
        {
          title: "Calculation Coordination",
          description: "Coordination of stress analysis across multiple vendors. We ensure consistency, quality control, and unified results for large-scale projects involving various software platforms.",
          icon: "🔄"
        },
        {
          title: "Custom Software Development and IT services",
          description: "Development of custom software and scripts to automate repetitive engineering tasks, speeding up your workflow and reducing data transfer errors.",
          icon: "💻"
        }
      ]

    },
    software: {
      title: "Software & Standards",
      software: {
        title: "Software"
      },
      pipeStandards: {
        title: "Pipe Stress Analysis Standards",
        items: [
          "EN 13480-3",
          "ASME B31.1",
          "ASME B31.3"
        ]
      },
      otherStandards: {
        title: "Other Calculation Standards",
        items: [
          "Flange connections – EN 1591-1",
          "FEM calculation – EN 13445-3",
          "FEM calculation – ASME Section VIII"
        ]
      }
    },
    about: {
      title: "About Us",
      description: "Small engineering company with over a decade of expertise in the energy, chemical, and petrochemical sectors. We deliver quality engineering solutions worldwide."
    },
    contact: {
      title: "Contact Us",
      description: "Ready to get started? Reach out to us today. \n References available upon request."
    },
    enlarge: "Enlarge",
    linkedinLabel: "Follow us on LinkedIn"
  },
  cs: {
    hero: {
      subtitle: "Technické analýzy a inženýrské služby v oblasti potrubí",
      cta: "Začněte s námi"
    },
    helpSection: {
      title: "Naše služby",
      items: [
        {
          title: "Potrubní výpočty",
          description: "Specializujeme se na potrubní výpočty a poskytujeme komplexní řešení od koncepční fáze až po detailní engineering.",
          icon: "🏭"
        },
        {
          title: "Vzdělávání a konzultace",
          description: "Nabízíme specializované školení a konzultace v oblasti projekce a výpočtů potrubí.",
          icon: "⚡"
        },
        {
          title: "Speciální služby",
          description: "Máme zkušenosti s koordinací pevnostních výpočtů a s vývojem softwarových nástrojů na míru přizpůsobeným vašim inženýrským potřebám.",
          icon: "🏢"
        }
      ]
    },
    services: {
      title: "Naše služby v detailu",
      items: [
        {
          title: "Napěťové a pevnostní analýzy",
          description: "Provádíme komplexní napěťové a pevnostní analýzy potrubí podle norem EN 13480‑3, ASME B31.1 a ASME B31.3. Zaměřujeme se na přesné FEM výpočty, kontroly těsnosti přírub, posouzení zatížení hrdel a další inženýrské úkony, které zajišťují bezpečný, spolehlivý a efektivní provoz potrubních systémů.",
          icon: "📊"
        },
        {
          title: "Optimalizace trasování",
          description: "Návrh a optimalizace trasování potrubí s ohledem na vzdálenost podpěr a tepelnou dilataci. Cílem je minimalizace sil na hrdla a výběr efektivního systému uložení (závěsy, podpěry).",
          icon: "🔧"
        },
        {
          title: "Návrh potrubních tříd",
          description: "Tvorba a verifikace potrubních tříd. Provádíme veškeré pevnostní výpočty tlouštěk stěn a komponent pro splnění požadavků norem.",
          icon: "⚡"
        },
        {
          title: "Konzultace a školení",
          description: "Odborné poradenství v oblasti stress analýzy a metodiky navrhování. Nabízíme školení pro software CAESAR II, ROHR2 a LICAD zaměřené na reálné inženýrské problémy.",
          icon: "💡"
        },
        {
          title: "Koordinace výpočtů",
          description: "Koordinace a sjednocení výstupů od různých dodavatelů výpočtů. Zajišťujeme konzistenci dat a výsledků napříč celým projektem.",
          icon: "🔄"
        },
        {
          title: "Vývoj software a IT služby",
          description: "Tvorba softwaru a skriptů na míru pro automatizaci výpočtů a zefektivnění inženýrských procesů.",
          icon: "💻"
        }
      ]
    },
    software: {
      title: "Software a normy",
      software: {
        title: "Software"
      },
      pipeStandards: {
        title: "Normy pro napěťovou analýzu potrubí",
        items: [
          "EN 13480-3",
          "ASME B31.1",
          "ASME B31.3"
        ]
      },
      otherStandards: {
        title: "Další výpočetní normy",
        items: [
          "Přírubové spoje – EN 1591-1",
          "MKP výpočet – EN 13445-3",
          "MKP výpočet – ASME Section VIII"
        ]
      }
    },
    about: {
      title: "O nás",
      description: "Malá inženýrská firma s více než 10letou zkušeností v energetickém, chemickém a petrochemickém průmyslu. Dodáváme kvalitní inženýrská řešení po celém světě."
    },
    contact: {
      title: "Kontaktujte nás",
      description: "Jste připraveni začít? Ozvěte se nám ještě dnes.  \n Reference poskytneme na vyžádání."
    },
    enlarge: "Zvětšit",
    linkedinLabel: "Sledujte nás na LinkedIn"
  },
  de: {
    hero: {
      subtitle: "Technische Analysen und Piping Engineering",
      cta: "Beginnen Sie jetzt"
    },
    helpSection: {
      title: "Unsere Dienstleistungen",
      items: [
        {
          title: "Rohrstatik & Berechnung",
          description: "Wir sind spezialisiert auf komplexe Rohrleitungsberechnungen, von der Konzeptphase bis zum Detail-Engineering.",
          icon: "🏭"
        },
        {
          title: "Schulung & Beratung",
          description: "Wir bieten spezialisierte Schulungen und Beratungen im Bereich Rohrleitungsplanung und -berechnung an.",
          icon: "⚡"
        },
        {
          title: "Spezielle Dienstleistungen",
          description: "Erfahrungen in der Koordination von Spannungsanalysen und der Entwicklung maßgeschneiderter Softwaretools.",
          icon: "🏢"
        }
      ]
    },
    services: {
      title: "Unsere Dienstleistungen im Detail",
      items: [
        {
          title: "Rohrspannungsanalyse & Flexibilitätsbewertung",
          description: "Strukturmechanische Rohranalysen einschließlich detaillierter FEM-Berechnungen für Komponenten und Flanschverbindungen.",
          icon: "📊"
        },
        {
          title: "Optimierung der Rohrleitungsführung",
          description: "Optimierung von Rohrleitungsverläufen, Stutzenlasten und Halterungskonzepten für effiziente und sichere Systeme.",
          icon: "🔧"
        },
        {
          title: "Rohrklassen – Auslegung & Nachweise",
          description: "Erstellung und Überprüfung von Rohrklassen einschließlich aller notwendigen Festigkeitsberechnungen.",
          icon: "⚡"
        },
        {
          title: "Beratung & Schulung",
          description: "Expertenberatung zu Rohrleitungsplanungsprozessen sowie Schulungen für führende Softwaretools wie CAESAR II, ROHR2, LICAD und mehr.",
          icon: "💡"
        },
        {
          title: "Koordination von Berechnungen",
          description: "Koordination zwischen verschiedenen Dienstleistern und Softwareplattformen zur Sicherstellung von Qualität und Konsistenz.",
          icon: "🔄"
        },
        {
          title: "Individuelle Softwareentwicklung",
          description: "Entwicklung maßgeschneiderter Softwarelösungen für Ihre spezifischen technischen Herausforderungen.",
          icon: "💻"
        }
      ]
    },
    software: {
      title: "Software & Normen",
      software: {
        title: "Software"
      },
      pipeStandards: {
        title: "Normen für Rohrstatik",
        items: [
          "EN 13480-3",
          "ASME B31.1",
          "ASME B31.3"
        ]
      },
      otherStandards: {
        title: "Weitere Berechnungsnormen",
        items: [
          "Flanschverbindungen – EN 1591-1",
          "FEM-Berechnung – EN 13445-3",
          "FEM-Berechnung – ASME Section VIII"
        ]
      }
    },
    about: {
      title: "Über uns",
      description: "Kleines Ingenieurbüro mit über 10 Jahren Erfahrung in der Energie-, Chemie- und Petrochemieindustrie. Wir liefern hochwertige Ingenieurlösungen weltweit."
    },
    contact: {
      title: "Kontaktieren Sie uns",
      description: "Bereit für den Start? Kontaktieren Sie uns noch heute. \n Referenzen auf Anfrage."
    },
    enlarge: "Vergrößern",
    linkedinLabel: "Folgen Sie uns auf LinkedIn"
  }
};


const ModernBeystreng = ({ language }) => {
  const email = "info";
  const domain = "beystreng.com";

  const [isVisible, setIsVisible] = useState({});


  useEffect(() => {
    const isSnap = navigator.userAgent.includes("ReactSnap");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isSnap) {
            // During pre-rendering, everything is visible
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          } else {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: entry.isIntersecting
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('section').forEach((section) => {
      if (section.id) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, []);

  const location = useLocation();

  // Automatic page tracking
  useEffect(() => {
    logPageView(location.pathname);
  }, [location]);

  const t = translations[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Language Selector */}
      <div className="fixed top-4 right-4 z-[100001]">
        <Link
          to={`/${language === 'cs' ? 'en' : (language === 'en' ? 'de' : 'cs')}`}
          onClick={() => {
            const nextLang = language === 'cs' ? 'en' : (language === 'en' ? 'de' : 'cs');
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.setItem('user_lang_pref', nextLang);
            }
            trackEvent("Engagement", "Language Switch", language);
          }}
          className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors"
        >
          <Globe className="w-5 h-5" />
          <span>{language.toUpperCase()}</span>
        </Link>
      </div>

















      {/* Hero Section with Logo Zoom */}
      <section className="relative h-screen flex items-center justify-center text-white">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-slate-950 to-blue-950 opacity-95"></div>


        <div className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 md:mb-10 bg-gradient-to-r from-[#40E0B5] to-[#00915F] text-transparent bg-clip-text drop-shadow-2xl">BEYSTRENG</h1>

          {/* Logo with zoom effect */}
          <div className="relative group cursor-pointer w-32 md:w-40 mx-auto mb-8">
            <div className="transition-all duration-500 ease-in-out transform group-hover:scale-125">
              <img
                src={process.env.PUBLIC_URL + '/logo.svg'}
                alt="BEYSTRENG Logo"
                className="w-full h-auto"
              />
            </div>
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 ease-in-out bg-green-500 blur-xl -z-10"
            />
          </div>

          <p className="text-lg md:text-xl mb-8 max-w-lg mx-auto">{t.hero.subtitle}</p>
          <button
            onClick={() => {
              trackEvent("Engagement", "CTA Click", "Hero - Get Started");
              const helpSection = document.getElementById('help');
              if (helpSection) {
                helpSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-blue-800 px-8 py-3 rounded-full font-semibold hover:bg-blue-100 transition duration-300"
          >
            {t.hero.cta}
          </button>

        </div>
      </section>

      {/* What We Can Help You Section */}
      <section id="help" className="py-12 sm:py-16 md:py-20 px-4 bg-blue-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16 text-gray-800">{t.helpSection.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {t.helpSection.items.map((help, index) => (
              <div
                key={index}
                className={`bg-white p-8 rounded-lg shadow-lg transform 
                           hover:-translate-y-2 transition duration-300
                           ${isVisible['help'] ? 'opacity-100' : 'opacity-0'}`}
              >
                <div className="text-4xl mb-4">{help.icon}</div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">{help.title}</h3>
                <p className="text-gray-600">{help.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 md:py-20 px-4 max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16 text-gray-800">{t.services.title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t.services.items.map((service, index) => (
            <div
              key={index}
              className={`bg-white p-8 rounded-lg shadow-lg transform 
                         hover:-translate-y-2 transition duration-300
                         ${isVisible['services'] ? 'opacity-100' : 'opacity-0'}`}
            >
              <div className="text-4xl mb-4">{service.icon}</div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">{service.title}</h3>
              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Software Section */}
      <section id="software" className="bg-blue-50 py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 md:mb-16 text-gray-800">{t.software.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Software Subsection */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-6 text-gray-800">{t.software.software.title}</h3>
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-600">
                {[
                  { name: 'Rohr 2', link: 'https://www.rohr2.com/en/rohr2.html' },
                  { name: 'Caesar II', link: 'https://hexagon.com/products/caesar-ii' },
                  'Probad',
                  'Fe-Pipe',
                  'VVD',
                  'Licad',
                  'Flexperte',
                  'Navisworks',
                  'PDMS/E3D'
                ].map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    {typeof item === 'string' ? item : (
                      <a href={item.link} target="_blank" rel="noopener noreferrer"
                        className="hover:text-blue-600 transition-colors">
                        {item.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Pipe Stress Analysis Standards Subsection */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-6 text-gray-800">{t.software.pipeStandards.title}</h3>
              <ul className="space-y-3 text-gray-600">
                {t.software.pipeStandards.items.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Other Calculation Standards Subsection */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h3 className="text-xl font-semibold mb-6 text-gray-800">{t.software.otherStandards.title}</h3>
              <ul className="space-y-3 text-gray-600">
                {t.software.otherStandards.items.map((item, index) => (
                  <li key={index} className="flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>


      {/* About Section */}
      <section id="about" className="py-12 sm:py-16 md:py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8 text-gray-800">{t.about.title}</h2>
          <p className="text-lg md:text-xl text-gray-600">
            {t.about.description}
          </p>
        </div>

      </section>

      <ImageStrip language={language} />


      <section id="contact" className="bg-gradient-to-t from-slate-950 to-blue-950 opacity-95 text-white py-12 sm:py-16 md:py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 md:mb-8">{t.contact.title}</h2>
          <p className="text-lg md:text-xl mb-8">{t.contact.description}</p>
          <div className="flex flex-col items-center gap-4">
            <a href={`mailto:${email}@${domain}`}
              className="inline-block bg-blue-600 px-8 py-3 rounded-full font-semibold 
              hover:bg-blue-700 transition duration-300">
              {email}@{domain}
            </a>

            <ContactForm language={language} />

            <a
              href={"https://www.linkedin.com/company/beystreng"}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-blue-800 transition-colors"
              aria-label={t.linkedinLabel}
            >
              <svg className="w-20 h-20" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      {/* Footer */}
      <footer className="py-8 text-center text-gray-400 bg-slate-950 opacity-95 px-4">
        <p>&copy; 2025 BEYSTRENG s.r.o. | All Rights Reserved</p>
        <div className="mt-4">
          <p>Beystreng s.r.o.</p>
          <p>Na Kovárně 951/27, Plzeň 312 00, Czech Republic</p>
          <p>DIČ / VAT Number: CZ21421005</p>
          <p>u2f6dc2</p>

          <div className="flex justify-center gap-4 mt-4">
            <Link to={`/${language}/gdpr`} className="text-sm hover:text-white transition-colors">
              {language === 'cs' ? 'Ochrana osobních údajů (GDPR)' : (language === 'de' ? 'Datenschutzerklärung (DSGVO)' : 'Privacy Policy (GDPR)')}
            </Link>
            <button
              onClick={() => window.dispatchEvent(new Event('openCookieSettings'))}
              className="text-sm hover:text-white transition-colors text-gray-400"
            >
              {language === 'cs' ? 'Nastavení Cookies' : (language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings')}
            </button>
          </div>

          <a
            href={"https://www.linkedin.com/company/beystreng"}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-blue-800 transition-colors "
            aria-label={t.linkedinLabel}
          >
            <svg className="w-20 h-20 " fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
            </svg>
          </a>


        </div>
      </footer>
    </div>
  );
};

const metaDescriptions = {
  en: seoData.en.description,
  cs: seoData.cs.description,
  de: seoData.de.description
};

const SEOMetadata = ({ language = 'en' }) => {
  const data = seoData[language];

  return (
    <Helmet>
      <html lang={language} />
      {/* HTML Meta Tags */}
      <title>{data.title}</title>
      <meta name="description" content={data.description} />
      <meta name="keywords" content={data.keywords} />
      {/* Author and CEO */}
      <meta name="author" content="Jiří Kocáb, BEYSTRENG s.r.o." />
      <meta name="publisher" content="Jiří Kocáb, BEYSTRENG s.r.o." />
      <meta name="theme-color" content="#00915F" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      {/* Short meta description for search engines */}
      <meta name="abstract" content={metaDescriptions[language]} />

      {/* Facebook Meta Tags / Open Graph */}
      <meta property="og:url" content={`https://www.beystreng.com${language === 'en' ? '' : '/' + language}`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={data.title} />
      <meta property="og:description" content={data.description} />
      <meta property="og:image" content="https://www.beystreng.com/logo.svg" />
      <meta property="og:site_name" content="BEYSTRENG s.r.o." />
      <meta property="og:locale" content={language === 'cs' ? 'cs_CZ' : (language === 'de' ? 'de_DE' : 'en_US')} />
      {language === 'en' && <meta property="og:locale:alternate" content="cs_CZ" />}
      {language === 'en' && <meta property="og:locale:alternate" content="de_DE" />}
      <meta property="article:author" content="Jiří Kocáb" />

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta property="twitter:domain" content="beystreng.com" />
      <meta property="twitter:url" content={`https://www.beystreng.com${language === 'en' ? '' : '/' + language}`} />
      <meta name="twitter:title" content={data.title} />
      <meta name="twitter:description" content={data.description} />
      <meta name="twitter:image" content="https://www.beystreng.com/logo.svg" />
      <meta name="twitter:creator" content="@JiriKocab" />

      {/* Language alternates */}
      <link rel="alternate" hrefLang="en" href="https://www.beystreng.com/" />
      <link rel="alternate" hrefLang="cs" href="https://www.beystreng.com/cs" />
      <link rel="alternate" hrefLang="de" href="https://www.beystreng.com/de" />
      <link rel="alternate" hrefLang="x-default" href="https://www.beystreng.com/" />

      {/* Favicon tags */}
      <link rel="icon" type="image/svg+xml" href="https://www.beystreng.com/logo.svg" />
      <link rel="icon" type="image/png" sizes="32x32" href="https://www.beystreng.com/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="https://www.beystreng.com/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="https://www.beystreng.com/logo192.png" />

      {/* Canonical URL */}
      <link rel="canonical" href={`https://www.beystreng.com${language === 'en' ? '' : '/' + language}${window.location.pathname.endsWith('/gdpr') ? '/gdpr' : ''}`} />

      {/* Czech SEO tags */}
      {language === 'cs' && (
        <meta httpEquiv="content-language" content="cs" />
      )}
    </Helmet>
  );
};

const StructuredData = ({ language = 'en' }) => {
  const data = seoData[language];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "@id": "https://www.beystreng.com/#organization",
    "name": data.organization.name,
    "legalName": data.organization.legalName,
    "taxID": data.organization.taxID,
    "vatID": data.organization.vatID,
    "url": "https://www.beystreng.com",
    "logo": data.organization.logo,
    "image": data.organization.image,
    "foundingDate": data.organization.foundingDate,
    "description": metaDescriptions[language],
    "address": {
      "@type": "PostalAddress",
      ...data.organization.address
    },
    "sameAs": [
      "https://www.linkedin.com/company/beystreng"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "email": data.organization.email,
      "contactType": "customer service",
      "availableLanguage": ["English", "Czech", "German"]
    },
    "knowsAbout": [
      language === "cs"
        ? "Napěťové a pevnostní analýzy potrubí"
        : "Pipe Stress & Flexibility Analysis",
      language === "cs"
        ? "Optimalizace trasování a uložení"
        : "Routing & Support Optimization",
      language === "cs"
        ? "Návrh potrubních tříd"
        : "Pipe Class Design",
      language === "cs"
        ? "Konzultace a školení"
        : "Consulting & Training",
      language === "cs"
        ? "Koordinace výpočtů"
        : "Calculation Coordination",
      language === "cs"
        ? "Vývoj software a IT služby"
        : "Custom Software Development and IT services"
    ],
    "areaServed": [
      "CZ",
      "DE",
      "AT",
      "CH",
      "EU",
      "EEA",
      "Europe",
      "Worldwide",
      {
        "@type": "Continent",
        "name": "Europe"
      }
    ]
  };

  const breadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": language === "cs" ? "Domů" : (language === "de" ? "Startseite" : "Home"),
        "item": `https://www.beystreng.com${language === 'en' ? '' : '/' + language}`
      }
    ]
  };

  if (window.location.pathname.endsWith('/gdpr')) {
    breadcrumbs.itemListElement.push({
      "@type": "ListItem",
      "position": 2,
      "name": language === "cs" ? "GDPR" : "GDPR",
      "item": `https://www.beystreng.com/${language}/gdpr`
    });
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbs)}
      </script>
    </Helmet>
  );
};

export { SEOMetadata, StructuredData };

// export default ModernBeystreng;

function LanguageWrapper() {
  const { lang: paramLang } = useParams();
  const lang = paramLang || 'en'; // Default to 'en' detection didn't redirect

  useEffect(() => {
    if (lang === "cs" || lang === "en" || lang === "de") {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('user_lang_pref', lang);
      }
    }
  }, [lang]);

  // Logic for automatic redirection based on browser language
  // ONLY run this if we are at the root path (paramLang is undefined)
  // and ONLY if we are running in the browser (not pre-rendering)
  if (!paramLang) {
    const isSnap = typeof navigator !== 'undefined' && navigator.userAgent.includes("ReactSnap");

    if (!isSnap) {
      // Check if user has an explicit preference set in this session
      const pref = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('user_lang_pref') : null;

      if (pref) {
        // If preference is explicitly set and not English, redirect to it
        if (pref !== 'en' && (pref === 'cs' || pref === 'de')) {
          return <Navigate to={`/${pref}`} replace />;
        }
        // If it's 'en', stay at root (do nothing)
      } else {
        const userLang = navigator.language || navigator.userLanguage || 'en';
        const langCode = userLang.toLowerCase();

        if (langCode.startsWith('cs') || langCode.startsWith('sk')) {
          return <Navigate to="/cs" replace />;
        }
        if (langCode.startsWith('de')) {
          return <Navigate to="/de" replace />;
        }
      }
    }
  }


  if (lang === "cs" || lang === "en" || lang === "de") {
    // Redirect /en to / for canonicalization
    if (lang === 'en' && paramLang === 'en') {
      return <Navigate to="/" replace />;
    }

    return (
      <div className="site-root-wrapper">
        <SEOMetadata language={lang} />
        <StructuredData language={lang} />
        <ModernBeystreng language={lang} />
        <CookieConsent language={lang} />
      </div>
    );
  }

  // If language is unknown (e.g. /404.html, /de, /random), show 404 instead of redirecting
  return <NotFound />;
}

function GDPRWrapper() {
  const { lang } = useParams();

  useEffect(() => {
    if (lang === "cs" || lang === "en" || lang === "de") {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem('user_lang_pref', lang);
      }
    }
  }, [lang]);

  if (lang === "cs" || lang === "en" || lang === "de") {
    return (
      <>
        <SEOMetadata language={lang} />
        <GDPR language={lang} />
      </>
    );
  }
  return <Navigate to="/en/gdpr" replace />;
}



function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<LanguageWrapper />} />
        <Route path="/gdpr" element={<Navigate to="/en/gdpr" replace />} />
        <Route path="/:lang/gdpr" element={<GDPRWrapper />} />
        <Route path="/:lang" element={<LanguageWrapper />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
